import { useState, useEffect, useCallback } from "react";
import { Wallet, Check, X, MessageSquare, Trash2, Euro, Clock, CheckCircle2, XCircle, Loader2, Mail, User, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "./adminApi";

const STATUS_META = {
  pending: { label: "En attente", cls: "bg-amber-100 text-amber-800 border-amber-300", Icon: Clock },
  approved: { label: "Approuvée", cls: "bg-green-100 text-green-800 border-green-300", Icon: CheckCircle2 },
  rejected: { label: "Refusée", cls: "bg-red-100 text-red-800 border-red-300", Icon: XCircle },
};

export default function AcompteTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [decisionFor, setDecisionFor] = useState(null); // { item, decision: 'approved'|'rejected' }
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter === "all" ? {} : { status: statusFilter };
      const r = await api.get("/admin/acompte-requests", { params });
      setItems(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
      setItems([]);
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openDecision = (item, decision) => {
    setDecisionFor({ item, decision });
    setComment("");
  };

  const submitDecision = async () => {
    if (!decisionFor) return;
    if (decisionFor.decision === "rejected" && !comment.trim()) {
      toast.error("Merci d'indiquer un motif de refus");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/acompte-requests/${decisionFor.item.request_id}`, {
        status: decisionFor.decision,
        admin_comment: comment.trim(),
      });
      toast.success(`Demande ${decisionFor.decision === "approved" ? "approuvée" : "refusée"} · email envoyé au candidat`);
      setDecisionFor(null);
      setComment("");
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer définitivement la demande de ${item.user_name} (${item.amount.toFixed(2)} €) ?`)) return;
    try {
      await api.delete(`/admin/acompte-requests/${item.request_id}`);
      toast.success("Demande supprimée");
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const counts = {
    pending: items.filter((i) => i.status === "pending").length,
    approved: items.filter((i) => i.status === "approved").length,
    rejected: items.filter((i) => i.status === "rejected").length,
  };

  return (
    <div className="space-y-4" data-testid="acompte-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-[#662483]" />
          <h2 className="text-lg font-bold text-[#3F3F3F]">Demandes d&apos;acompte</h2>
          <Badge className="bg-[#F0E9F3] text-[#662483] rounded-none ml-1" data-testid="acompte-total-count">{items.length}</Badge>
        </div>
      </div>

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-2">
        {[
          { key: "all", label: "Toutes" },
          { key: "pending", label: `En attente (${counts.pending})` },
          { key: "approved", label: `Approuvées (${counts.approved})` },
          { key: "rejected", label: `Refusées (${counts.rejected})` },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={`px-3 py-1.5 text-xs font-semibold border transition-colors ${
              statusFilter === f.key ? "bg-[#662483] text-white border-[#662483]" : "bg-white text-gray-600 border-gray-200 hover:border-[#662483]/40"
            }`}
            data-testid={`acompte-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && <p className="text-xs text-gray-500 italic">Chargement…</p>}
      {!loading && items.length === 0 && (
        <div className="text-center py-8 text-sm text-gray-500 border border-dashed border-gray-200 bg-white" data-testid="acompte-empty">
          <Wallet className="w-8 h-8 mx-auto text-gray-300 mb-2" />
          Aucune demande {statusFilter !== "all" && `(${STATUS_META[statusFilter]?.label.toLowerCase()})`}.
        </div>
      )}

      {/* Table / cards */}
      {!loading && items.length > 0 && (
        <div className="space-y-2" data-testid="acompte-list">
          {items.map((it) => {
            const meta = STATUS_META[it.status] || STATUS_META.pending;
            return (
              <div key={it.request_id} className="bg-white border border-gray-200 p-3" data-testid={`acompte-row-${it.request_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <User className="w-3.5 h-3.5 text-[#662483]" />
                      <span className="text-sm font-bold text-[#3F3F3F]">{it.user_name}</span>
                      <Badge className={`rounded-none text-[10px] border ${meta.cls}`} data-testid={`acompte-status-${it.request_id}`}>
                        <meta.Icon className="w-2.5 h-2.5 inline mr-0.5" /> {meta.label}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-0.5"><Mail className="w-3 h-3 inline mr-1" />{it.user_email}</p>
                    <div className="mt-2 flex items-center gap-3 flex-wrap">
                      <span className="text-base font-black text-[#0069B4]"><Euro className="w-4 h-4 inline mr-0.5" />{it.amount.toFixed(2)}</span>
                      <span className="text-[11px] text-gray-500"><Calendar className="w-3 h-3 inline mr-0.5" />Demandé le {new Date(it.created_at).toLocaleDateString("fr-FR")}</span>
                      {it.requested_date && (
                        <span className="text-[11px] text-gray-500">→ souhaité le {new Date(it.requested_date).toLocaleDateString("fr-FR")}</span>
                      )}
                    </div>
                    <div className="mt-2 text-[11px] text-gray-700 bg-[#FAFAFA] px-2 py-1 flex gap-1">
                      <MessageSquare className="w-3 h-3 text-gray-400 mt-0.5 shrink-0" />
                      <span>{it.reason}</span>
                    </div>
                    {it.admin_comment && (
                      <div className={`mt-1 text-[11px] px-2 py-1 border-l-2 ${it.status === "approved" ? "bg-green-50 border-green-500" : "bg-red-50 border-red-500"}`}>
                        <strong>Décision :</strong> {it.admin_comment}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    {it.status === "pending" && (
                      <>
                        <Button
                          onClick={() => openDecision(it, "approved")}
                          size="sm"
                          className="rounded-none bg-green-600 hover:bg-green-700 text-white text-xs h-8"
                          data-testid={`acompte-approve-${it.request_id}`}
                        >
                          <Check className="w-3.5 h-3.5 mr-1" /> Approuver
                        </Button>
                        <Button
                          onClick={() => openDecision(it, "rejected")}
                          size="sm"
                          variant="outline"
                          className="rounded-none border-red-500 text-red-600 hover:bg-red-50 text-xs h-8"
                          data-testid={`acompte-reject-${it.request_id}`}
                        >
                          <X className="w-3.5 h-3.5 mr-1" /> Refuser
                        </Button>
                      </>
                    )}
                    <button
                      onClick={() => handleDelete(it)}
                      className="p-1.5 hover:bg-red-50 text-red-500"
                      title="Supprimer"
                      data-testid={`acompte-delete-${it.request_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Decision dialog */}
      <Dialog open={!!decisionFor} onOpenChange={(v) => !v && setDecisionFor(null)}>
        <DialogContent className="rounded-none sm:max-w-md" data-testid="acompte-decision-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#3F3F3F]">
              {decisionFor?.decision === "approved" ? "Approuver la demande" : "Refuser la demande"}
            </DialogTitle>
          </DialogHeader>
          {decisionFor && (
            <div className="space-y-3 mt-2">
              <div className="bg-[#FAFAFA] border border-gray-200 p-3">
                <p className="text-sm font-bold text-[#3F3F3F]">{decisionFor.item.user_name} · <span className="text-[#0069B4]">{decisionFor.item.amount.toFixed(2)} €</span></p>
                <p className="text-[11px] text-gray-600 mt-1">{decisionFor.item.reason}</p>
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-700 mb-1 block">
                  Commentaire {decisionFor.decision === "rejected" && <span className="text-red-500">*</span>}
                </Label>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  placeholder={decisionFor.decision === "approved" ? "ex. Versement programmé pour le vendredi 15" : "ex. Impossible d'anticiper le versement pour ce mois-ci"}
                  className="rounded-none text-sm"
                  data-testid="acompte-decision-comment"
                />
                <p className="text-[10px] text-gray-500 mt-0.5">Ce commentaire sera inclus dans l&apos;email envoyé au candidat.</p>
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  onClick={submitDecision}
                  disabled={saving}
                  className={`flex-1 rounded-none text-white font-semibold ${decisionFor.decision === "approved" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}
                  data-testid="acompte-decision-submit"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (decisionFor.decision === "approved" ? <Check className="w-4 h-4 mr-2" /> : <X className="w-4 h-4 mr-2" />)}
                  {saving ? "..." : (decisionFor.decision === "approved" ? "Approuver et notifier" : "Refuser et notifier")}
                </Button>
                <Button variant="outline" onClick={() => setDecisionFor(null)} disabled={saving} className="rounded-none">Annuler</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
